/**
 * 指定フォーマット出力（#3070）の未保存変更を、パターン保存時にまとめて反映する。
 *
 * 画面上では Firestore / Storage に書き込まず、パターンを保存したときに初めて確定させる。
 * 他の設定項目（項目名・共有範囲など）と挙動を揃えるため。
 */
import * as Sentry from '@sentry/react';
import {
  postExportTemplate,
  updateExportTemplate,
  replaceExportTemplateFile,
  deleteExportTemplate,
  getExportTemplate,
} from '../../api/firebase/firestore/exportTemplate';
import { type PendingExportTemplateChanges } from '../../components/uiParts/parts/ExportTemplateManager';

export interface CommitExportTemplatesParams {
  pending: PendingExportTemplateChanges;
  patternId: string;
  companyId: string;
  userId: string;
  userName: string;
}

/**
 * 未保存の追加・削除・シート変更をまとめて反映する。
 * 1件失敗しても他は進め、最後にまとめて例外にする（部分的に反映されるのを避けられないため、
 * どこまで進んだかを呼び出し側が把握できるようにする）。
 */
export const commitExportTemplates = async ({
  pending,
  patternId,
  companyId,
  userId,
  userName,
}: CommitExportTemplatesParams): Promise<void> => {
  const failures: string[] = [];

  // 追加
  for (const added of pending.added) {
    try {
      await postExportTemplate(added.file, {
        pattern_id: patternId,
        template_name: added.templateName,
        company_id: companyId,
        target_sheets: added.targetSheets,
        all_sheets: added.allSheets,
        used_field_codes: added.usedFieldCodes,
        created_by: userId,
        created_by_name: userName,
      });
    } catch (e) {
      // 原因（権限エラー・容量超過など）が消えると調査できないため必ず記録する
      Sentry.captureException(e);
      failures.push(`「${added.templateName}」の登録`);
    }
  }

  // ファイルの差し替え（ドキュメントIDは変えない）
  for (const replaced of pending.replaced) {
    if (pending.removedIds.includes(replaced.id)) continue;
    try {
      const template = await getExportTemplate(replaced.id);
      if (!template) {
        failures.push(`「${replaced.templateName}」の差し替え`);
        continue;
      }
      await replaceExportTemplateFile(template, replaced.file, {
        template_name: replaced.templateName,
        target_sheets: replaced.targetSheets,
        all_sheets: replaced.allSheets,
        used_field_codes: replaced.usedFieldCodes,
      });
    } catch (e) {
      Sentry.captureException(e);
      failures.push(`「${replaced.templateName}」の差し替え`);
    }
  }

  // 対象シートの変更
  for (const updated of pending.updatedSheets) {
    // 同じ保存で削除されるものは触らない
    if (pending.removedIds.includes(updated.id)) continue;
    try {
      await updateExportTemplate(updated.id, { target_sheets: updated.targetSheets });
    } catch (e) {
      Sentry.captureException(e);
      failures.push('対象シートの変更');
    }
  }

  // 削除
  for (const removedId of pending.removedIds) {
    try {
      const template = await getExportTemplate(removedId);
      if (template) await deleteExportTemplate(template);
    } catch (e) {
      Sentry.captureException(e);
      failures.push('フォーマットの削除');
    }
  }

  if (failures.length > 0) {
    throw new Error(`次の処理に失敗しました: ${failures.join('、')}`);
  }
};

/** 未保存の変更があるか */
export const hasPendingExportTemplateChanges = (pending: PendingExportTemplateChanges): boolean =>
  pending.added.length > 0 ||
  pending.removedIds.length > 0 ||
  pending.replaced.length > 0 ||
  pending.updatedSheets.length > 0;
